import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import { message } from 'antd';

const CollaborativeListsContext = React.createContext();

const name = 'collaborativeListsDataStore';

export const useCollaborativeLists = () =>
	React.useContext(CollaborativeListsContext);

export const connectToCollaborativeLists = makeConnectTo(
	CollaborativeListsContext,
	name,
);

export default class CollaborativeListsDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	};

	state = {
		collaborativeListsData: {},
		isLoading: true,
	};

	render() {
		const collaborativeListsDataStore = this.getStore();
		return (
			<CollaborativeListsContext.Provider
				value={collaborativeListsDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(collaborativeListsDataStore)
					: this.props.children}
			</CollaborativeListsContext.Provider>
		);
	}

	componentDidMount() {
		this.fetchCollaborativeLists();
	}

	componentWillUnmount() {
		if (
			this.collaborativeListsFetch &&
			this.collaborativeListsFetch.cancel
		) {
			this.collaborativeListsFetch.cancel();
		}
	}

	getStore = (state = this.state) => ({
		state,
		refresh: () => this.refreshCollaborativeLists(),
		getListDetails: listId => this.getListDetails(listId),
		changeFilingStatus: listFiling => this.changeFilingStatus(listFiling),
		fetchCollaborativeListsByCommunity: communityId =>
			this.fetchCollaborativeListsByCommunity(communityId),
		createList: (values, onSuccess) => this.createList(values, onSuccess),
		updateList: (values, onSuccess) => this.updateList(values, onSuccess),
	});

	createList = (values, onSuccess) => {
		const endpoint = `/api/v1/collaborativeTitleList`;

		api.post(endpoint, values).then(
			() => this.refreshCollaborativeLists(),
			onSuccess(),
		);
	};

	updateList = (values, onSuccess) => {
		const endpoint = `/api/v1/collaborativeTitleList`;

		api.put(endpoint, values).then(
			() => this.refreshCollaborativeLists(),
			onSuccess(),
		);
	};

	refreshCollaborativeLists = (bypassLoad = false) => {
		if (!bypassLoad) {
			this.setState({ isLoading: true });
		}
		this.fetchCollaborativeLists();
		return this.collaborativeListsFetch.promise;
	};

	fetchCollaborativeLists = () => {
		const request = api.cancelableGet(`/api/v1/collaborativeTitleLists/0`);
		request.promise.then(this.cacheApiResponse);
		this.collaborativeListsFetch = request;
	};

	fetchCollaborativeListsByCommunity = communityId => {
		this.setState({ isLoading: true });
		const request = api.cancelableGet(
			`/api/v1/collaborativeTitleLists/${communityId}`,
		);
		request.promise.then(this.cacheApiResponse);
		this.collaborativeListsFetch = request;
	};

	cacheApiResponse = apiResponse => {
		const collaborativeListsData =
			apiResponse && this.getCollaborativeListsData(apiResponse);
		this.setState({ collaborativeListsData, isLoading: false });
	};

	getCollaborativeListsData = apiResponse => {
		const r = apiResponse;
		const mostRecentDates = r.mostRecentAdds
			.concat(r.mostRecentVotes)
			.concat(r.mostRecentComments)
			.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
		return {
			listItems: this.mergeImagesIntoProducts(
				r.coverListItems,
				r.productImageUrisBySku,
			),
			lists: this.buildListsByMostRecent(mostRecentDates, r).sort(
				(a, b) => new Date(b.mostRecent) - new Date(a.mostRecent),
			),
		};
	};

	getListDetails = listId => {
		return this.state.collaborativeListsData.lists.filter(
			list => Number(list.id) === Number(listId),
		)[0];
	};

	getListSummaries = r => {
		return r.votes;
	};

	mergeImagesIntoProducts(listItems, productImageUrisBySku) {
		return listItems.map(li => ({
			...li,
			image:
				li.sku in productImageUrisBySku &&
				productImageUrisBySku[li.sku],
		}));
	}

	buildListsByMostRecent(mostRecentDates, r) {
		const {
			lists,
			listItems,
			quantities,
			comments,
			votes,
			filings,
			userAvatarsByAppUserId,
			usersById,
		} = r;
		return lists.map(li => ({
			...li,
			listItems: listItems.filter(list => list.listId === li.id),
			mostRecent:
				mostRecentDates.filter(mrd => mrd.listId === li.id).length >
					0 &&
				mostRecentDates.filter(mrd => mrd.listId === li.id)[0]
					.createdDate,
			quantities: quantities.filter(qu => qu.listId === li.id),
			comments: comments.filter(com => com.listId === li.id),
			votes: votes.filter(com => com.listId === li.id),
			filing:
				filings.filter(fil => fil.listId === li.id).length > 0 &&
				filings.filter(fil => fil.listId === li.id)[0],
			userAvatar:
				userAvatarsByAppUserId[li.appUserId] &&
				userAvatarsByAppUserId[li.appUserId],
			user: usersById[li.appUserId] && usersById[li.appUserId],
		}));
	}

	changeFilingStatus(listFiling) {
		api.post('/api/v1/collaborativeTitleList/filing', listFiling).then(
			() => {
				this.refreshCollaborativeLists(true);
				message.success(listFiling.filingMessage);
			},
		);
	}
}
